/**
 * Lesson Sidebar Component
 * Displays the lesson's blocks in edit mode and supports reordering.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faLayerGroup, faLock } from '@fortawesome/free-solid-svg-icons';
import {
  useTheme,
  isBlockOverridden,
  isBlockSuppressed,
  isLessonCompleteBlock,
  type CourseBlock,
  type LessonBlockTranslationRecord,
} from '@course-authoring/shared-types';
import {
  accountAssignmentKeys,
  fetchAssignedAccounts,
  type ClientAccount,
} from '../api/accountAssignmentApi';
import { useClientContext } from '../hooks/useClientContext';
import { selectAllBlocks, useEditorStore } from '../store/editorStore';
import { getBlockDisplayTitle, getBlockTypeLabel } from '../utils/blockDisplay';
import { formatBlockHumanId } from '../utils/humanIds';
import { BlockCustomizationPopover } from './BlockCustomizationPopover';
import { BlockTypeChooser } from './BlockTypeChooser';
import { createBlockFromType, type BlockTypeOption } from '../utils/blockFactory';
import { TranslationStatusBadge } from './TranslationStatusPrimitives';

const INSERT_CHOOSER_WIDTH = 280;
const INSERT_CHOOSER_HEIGHT = 380;

interface LessonSidebarProps {
  disabled?: boolean;
  /** When false (client context), the add-block affordance is hidden. */
  allowAddBlock?: boolean;
  translationRecords?: LessonBlockTranslationRecord[];
  /** Needed to resolve the assigned-account names shown in the customization popover (NA-295). */
  lessonId?: string;
  /**
   * `{ [blockId]: externalAccountId[] }`, English overrides only, currently-assigned
   * accounts only. Undefined outside Standard/English context — see LessonEditorPage's
   * fetch gate. Only present here (never re-derived from client-context/locale flags
   * inside this component) so this component doesn't need to reconcile the two
   * independently-gated conditions itself.
   */
  blockCustomizations?: Record<string, number[]>;
  blockCustomizationsLoading?: boolean;
  blockCustomizationsError?: boolean;
  onRetryBlockCustomizations?: () => void;
}

interface SortableLessonBlockRowProps {
  block: CourseBlock;
  index: number;
  disabled: boolean;
  /** Disables drag-reorder only (selection/editing stays enabled). */
  dragDisabled: boolean;
  showDragHandle: boolean;
  isActive: boolean;
  translationStatus?: LessonBlockTranslationRecord['status'];
  /** In client context, show hide/re-enable controls and suppressed styling. */
  clientContextActive: boolean;
  /** Count of currently-assigned accounts with an English override on this block (NA-295). */
  customizationAccountCount: number;
  isCustomizationPopoverOpen: boolean;
  styles: Record<string, CSSProperties>;
  onActivate: (blockId: string) => void;
  onReEnable: (blockId: string) => void;
  onToggleCustomizationPopover: (
    blockId: string,
    anchor: HTMLElement,
    trigger: HTMLElement,
  ) => void;
}

interface SidebarInsertControlProps {
  insertIndex: number;
  disabled: boolean;
  isOpen: boolean;
  chooserPosition: { x: number; y: number } | null;
  styles: Record<string, CSSProperties>;
  ariaLabel: string;
  buttonLabel: string;
  onOpen: (insertIndex: number, target: HTMLElement) => void;
  onClose: () => void;
  onSelect: (type: BlockTypeOption, insertIndex: number) => void;
  excludeBlockTypes?: string[];
}

function SidebarInsertControl({
  insertIndex,
  disabled,
  isOpen,
  chooserPosition,
  styles,
  ariaLabel,
  buttonLabel,
  onOpen,
  onClose,
  onSelect,
  excludeBlockTypes,
}: SidebarInsertControlProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isInsertButtonVisible = isHovered || isOpen || isFocused;

  return (
    <div
      style={{
        ...styles.insertControl,
        ...(isOpen ? styles.insertControlOpen : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => !isOpen && setIsHovered(false)}
    >
      <div
        style={{
          ...styles.insertButtonWrapper,
          opacity: isInsertButtonVisible ? 1 : 0,
          pointerEvents: isInsertButtonVisible ? 'auto' : 'none',
        }}
      >
        <button
          type="button"
          style={{
            ...styles.insertButton,
            ...(disabled ? styles.insertButtonDisabled : {}),
          }}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={(event) => onOpen(insertIndex, event.currentTarget)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            if (!isOpen) {
              setIsHovered(false);
            }
          }}
        >
          {buttonLabel}
        </button>
      </div>
      {isOpen && chooserPosition
        ? createPortal(
            <BlockTypeChooser
              onSelect={(type) => onSelect(type, insertIndex)}
              onClose={onClose}
              position={chooserPosition}
              excludeBlockTypes={excludeBlockTypes}
            />,
            document.body
          )
        : null}
    </div>
  );
}

function SortableLessonBlockRow({
  block,
  index,
  disabled,
  dragDisabled,
  showDragHandle,
  isActive,
  translationStatus,
  clientContextActive,
  customizationAccountCount,
  isCustomizationPopoverOpen,
  styles,
  onActivate,
  onReEnable,
  onToggleCustomizationPopover,
}: SortableLessonBlockRowProps) {
  // The lock protects a block's content, not its position: locked blocks
  // remain draggable and selectable, with an icon marking the locked state.
  const isRowLocked = block.locked === true;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: dragDisabled,
  });

  const displayTitle = getBlockDisplayTitle(block);
  const blockHumanId = formatBlockHumanId(block.id);
  const typeLabel = blockHumanId
    ? `${getBlockTypeLabel(block)} · ${blockHumanId}`
    : getBlockTypeLabel(block);
  const isSuppressed = isBlockSuppressed(block);
  // Shown independent of lock state — the lesson-list badge stays visible even
  // while the lesson is published/locked (NA-199).
  const isCustomized = isBlockOverridden(block);
  // Mutually exclusive with isCustomized: a client-added block has no
  // Emtrain standard version, so it never counts as "Customized" (see
  // isBlockOverridden), and gets its own badge instead.
  const isClientAdded = block.clientAdded === true;

  const rowStyle: CSSProperties = {
    ...styles.blockRow,
    ...(isActive ? styles.blockRowActive : {}),
    ...(isSuppressed ? styles.blockRowSuppressed : {}),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : isSuppressed ? 0.55 : 1,
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate(block.id);
    }
  };

  // A published lock disables edits and structural controls, not navigation:
  // authors must still be able to select and scroll to readable blocks.
  // Suppressed client blocks remain non-selectable because they are not part
  // of that account's rendered lesson.
  const activationDisabled = clientContextActive && isSuppressed;

  // Base blocks are not reorderable in client context, so their drag handle is
  // hidden (kept in layout to preserve the row's gutter alignment).
  const dragHandleHidden = clientContextActive && !block.clientAdded;

  return (
    <div ref={setNodeRef} style={rowStyle} role="listitem">
      {showDragHandle ? (
        <button
          type="button"
          style={{
            ...styles.dragHandle,
            ...(dragHandleHidden
              ? styles.dragHandleHidden
              : dragDisabled
                ? styles.dragHandleDisabled
                : {}),
          }}
          aria-label={`Drag to reorder ${displayTitle}`}
          onClick={(event) => event.stopPropagation()}
          disabled={dragDisabled}
          {...listeners}
          {...attributes}
        >
          <FontAwesomeIcon icon={faGripVertical} style={{ fontSize: 16 }} />
        </button>
      ) : null}

      <div
        style={styles.rowContent}
        role="button"
        tabIndex={activationDisabled ? -1 : 0}
        aria-current={isActive ? 'true' : undefined}
        onClick={() => !activationDisabled && onActivate(block.id)}
        onKeyDown={handleKeyDown}
      >
        <div style={styles.rowIndex}>{index + 1}</div>
        <div style={styles.rowText}>
          <div style={styles.rowTitle}>
            {isRowLocked && (
              <span style={styles.rowLockIcon} title="Locked block">
                <FontAwesomeIcon icon={faLock} />
              </span>
            )}
            {displayTitle}
            {isCustomized && <span style={styles.customizedBadge}>Customized</span>}
            {isClientAdded && <span style={styles.clientAddedBadge}>Account-added</span>}
          </div>
          <div style={styles.rowSubtitleRow}>
            <div style={styles.rowType}>{typeLabel}</div>
            {customizationAccountCount > 0 && (
              <button
                type="button"
                style={styles.customizationChip}
                aria-label={`Customized in ${customizationAccountCount} account${customizationAccountCount === 1 ? '' : 's'}`}
                aria-haspopup="dialog"
                aria-expanded={isCustomizationPopoverOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  onActivate(block.id);
                  const card = event.currentTarget.closest('[role="listitem"]');
                  onToggleCustomizationPopover(
                    block.id,
                    (card as HTMLElement) ?? event.currentTarget,
                    event.currentTarget,
                  );
                }}
              >
                <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 10 }} />
                {customizationAccountCount}
              </button>
            )}
          </div>
          {clientContextActive && isSuppressed ? (
            <div style={styles.rowDisabledNote}>this block is disabled</div>
          ) : null}
        </div>
        {translationStatus ? (
          <span style={styles.translationStatus}>
            <TranslationStatusBadge status={translationStatus} missingLabel="Not Translated" />
          </span>
        ) : null}
      </div>

      {clientContextActive && isSuppressed ? (
        <button
          type="button"
          style={styles.clientActionButton}
          aria-label={`Re-enable ${displayTitle}`}
          onClick={(event) => {
            event.stopPropagation();
            onReEnable(block.id);
          }}
        >
          Re-enable
        </button>
      ) : null}
    </div>
  );
}

export function LessonSidebar({
  disabled = false,
  allowAddBlock = true,
  translationRecords,
  lessonId,
  blockCustomizations,
  blockCustomizationsLoading = false,
  blockCustomizationsError = false,
  onRetryBlockCustomizations,
}: LessonSidebarProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const { clientContext, setClientContext } = useClientContext();
  const [openCustomizationBlockId, setOpenCustomizationBlockId] = useState<string | null>(null);
  const [customizationPopoverPosition, setCustomizationPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [customizationPopoverTrigger, setCustomizationPopoverTrigger] = useState<HTMLElement | null>(null);
  // Set only while waiting for a context switch triggered from the popover to
  // land — see the effect below for why matching clientContext isn't a
  // guaranteed "the new account's lesson data has loaded" signal, just the
  // best one available without new cross-component plumbing.
  const pendingContextSelectionRef = useRef<{ blockId: string; accountId: string } | null>(null);
  const {
    data: assignedAccounts,
    isLoading: assignedAccountsLoading,
    isError: assignedAccountsError,
    refetch: refetchAssignedAccounts,
  } = useQuery({
    queryKey: accountAssignmentKeys.assignedAccounts('lesson', lessonId ?? ''),
    queryFn: () => fetchAssignedAccounts('lesson', lessonId!),
    enabled: Boolean(lessonId) && Boolean(blockCustomizations),
  });
  const [openInsertIndex, setOpenInsertIndex] = useState<number | null>(null);
  const [chooserPosition, setChooserPosition] = useState<{ x: number; y: number } | null>(null);
  const allBlocks = useEditorStore(selectAllBlocks);
  const translationMode = translationRecords !== undefined;
  const structuralControlsEnabled = allowAddBlock && !translationMode;
  const translationRecordsByBlockId = useMemo(
    () => new Map(translationRecords?.map((record) => [record.blockId, record])),
    [translationRecords],
  );
  const blocks = useMemo(
    () => (translationRecords?.map((record) => record.sourceBlock) ?? allBlocks)
      .filter((block) => !isLessonCompleteBlock(block)),
    [allBlocks, translationRecords],
  );
  const activeSelection = useEditorStore((state) => state.activeSelection);
  const addBlock = useEditorStore((state) => state.addBlock);
  const reorderBlocks = useEditorStore((state) => state.reorderBlocks);
  const setActiveSelection = useEditorStore((state) => state.setActiveSelection);
  const storeClientContextActive = useEditorStore((state) => state.clientContextActive);
  const clientContextActive = storeClientContextActive && !translationMode;
  const reEnableBlock = useEditorStore((state) => state.reEnableBlock);
  // In client context only account-created (client-added) blocks are
  // reorderable; base blocks keep their order and expose no drag handle.
  // Selection/editing stays enabled for all blocks.
  const isBlockDragDisabled = useCallback(
    (block: CourseBlock) => translationMode || disabled || (clientContextActive && !block.clientAdded),
    [clientContextActive, disabled, translationMode],
  );

  const scrollBlockIntoView = useCallback((blockId: string) => {
    requestAnimationFrame(() => {
      const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
      if (blockElement instanceof HTMLElement) {
        blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleActivateBlock = useCallback(
    (blockId: string) => {
      setOpenInsertIndex(null);
      setChooserPosition(null);
      setActiveSelection(blockId);
      scrollBlockIntoView(blockId);
    },
    [scrollBlockIntoView, setActiveSelection]
  );

  const handleToggleCustomizationPopover = useCallback((
    blockId: string,
    anchor: HTMLElement,
    trigger: HTMLElement,
  ) => {
    setOpenCustomizationBlockId((current) => {
      if (current === blockId) {
        setCustomizationPopoverPosition(null);
        setCustomizationPopoverTrigger(null);
        return null;
      }
      const rect = anchor.getBoundingClientRect();
      setCustomizationPopoverPosition({ x: rect.left, y: rect.bottom + 4 });
      setCustomizationPopoverTrigger(trigger);
      return blockId;
    });
  }, []);

  const handleCloseCustomizationPopover = useCallback(() => {
    setOpenCustomizationBlockId(null);
    setCustomizationPopoverPosition(null);
    setCustomizationPopoverTrigger(null);
  }, []);

  const handleSelectCustomizationAccount = useCallback(
    (blockId: string, account: ClientAccount) => {
      handleCloseCustomizationPopover();
      pendingContextSelectionRef.current = { blockId, accountId: account.id };
      setClientContext(account);
    },
    [handleCloseCustomizationPopover, setClientContext]
  );

  // Applies the block selection stashed by handleSelectCustomizationAccount once
  // the content context has actually switched to the target account. Block ids
  // are stable across contexts (same base block, different resolved payload),
  // so this fires as soon as clientContext/store state reflects the switch —
  // it does not wait for the account-scoped lesson data to finish loading,
  // since there's no existing signal for that from here (see design.md's
  // Risks/Trade-offs: this is new plumbing, not a fully synchronized reuse).
  useEffect(() => {
    const pending = pendingContextSelectionRef.current;
    if (!pending) return;
    if (clientContext?.id !== pending.accountId || !storeClientContextActive) return;
    pendingContextSelectionRef.current = null;
    handleActivateBlock(pending.blockId);
  }, [clientContext, storeClientContextActive, handleActivateBlock]);

  const handleCloseInsertChooser = useCallback(() => {
    setOpenInsertIndex(null);
    setChooserPosition(null);
  }, []);

  const handleOpenInsertChooser = useCallback(
    (insertIndex: number, target: HTMLElement) => {
      if (disabled) {
        return;
      }

      if (openInsertIndex === insertIndex) {
        handleCloseInsertChooser();
        return;
      }

      const rect = target.getBoundingClientRect();
      const x = Math.max(16, Math.min(rect.left, window.innerWidth - INSERT_CHOOSER_WIDTH - 16));
      const y = Math.max(
        16,
        Math.min(rect.bottom + 8, window.innerHeight - INSERT_CHOOSER_HEIGHT - 16)
      );

      setOpenInsertIndex(insertIndex);
      setChooserPosition({ x, y });
    },
    [disabled, handleCloseInsertChooser, openInsertIndex]
  );

  const handleInsertBlock = useCallback(
    (type: BlockTypeOption, insertIndex: number) => {
      const block = createBlockFromType(type);
      const effectiveIndex = type === 'lesson-complete' ? undefined : insertIndex;
      addBlock(block, effectiveIndex);
      setActiveSelection(block.id);
      handleCloseInsertChooser();
      scrollBlockIntoView(block.id);
    },
    [addBlock, handleCloseInsertChooser, scrollBlockIntoView, setActiveSelection]
  );

  const lessonCompleteBlockId = useMemo(
    () => allBlocks.find((block) => isLessonCompleteBlock(block))?.id ?? null,
    [allBlocks],
  );

  // fetchAssignedAccounts already returns accounts sorted alphabetically by
  // name, so filtering preserves that order for the popover.
  const openPopoverAccounts = useMemo(() => {
    if (!openCustomizationBlockId || !assignedAccounts) return [];
    const accountIds = new Set(
      (blockCustomizations?.[openCustomizationBlockId] ?? []).map(String),
    );
    return assignedAccounts.filter((account) => accountIds.has(account.id));
  }, [openCustomizationBlockId, assignedAccounts, blockCustomizations]);
  const openCustomizationAccountCount = openCustomizationBlockId
    ? blockCustomizations?.[openCustomizationBlockId]?.length ?? 0
    : 0;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      handleCloseInsertChooser();

      if (translationMode) {
        return;
      }

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = blocks.findIndex((block) => block.id === active.id);
      const newIndex = blocks.findIndex((block) => block.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Only account-created blocks are reorderable in client context; base
      // blocks can't be picked up, so a drop that moves the account block past
      // them still leaves base blocks in their relative order.
      const activeBlock = blocks[oldIndex];
      if (!activeBlock || isBlockDragDisabled(activeBlock)) {
        return;
      }

      const newOrder = blocks.map((block) => block.id);
      const [movedBlockId] = newOrder.splice(oldIndex, 1);

      if (!movedBlockId) {
        return;
      }

      newOrder.splice(newIndex, 0, movedBlockId);
      if (lessonCompleteBlockId) {
        newOrder.push(lessonCompleteBlockId);
      }
      reorderBlocks(newOrder);
    },
    [blocks, isBlockDragDisabled, handleCloseInsertChooser, lessonCompleteBlockId, reorderBlocks, translationMode]
  );

  return (
    <aside style={styles.sidebar} aria-label="Lesson blocks">
      <div style={styles.header}>
        <div style={styles.headerCopy}>
          <h2 style={styles.title}>Blocks</h2>
        </div>
        <div style={styles.countBadge}>{`${blocks.length} total`}</div>
      </div>

      {blockCustomizationsLoading ? (
        <div role="status" style={styles.customizationQueryStatus}>
          Loading customization indicators…
        </div>
      ) : null}
      {blockCustomizationsError ? (
        <div role="alert" style={styles.customizationQueryStatus}>
          <span>Couldn’t load block customizations.</span>
          {onRetryBlockCustomizations ? (
            <button
              type="button"
              style={styles.customizationRetryButton}
              onClick={onRetryBlockCustomizations}
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {blocks.length > 0 ? (
        <div style={styles.listWrapper}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blocks.map((block) => block.id)}
              strategy={verticalListSortingStrategy}
            >
              <div
                style={{
                  ...styles.list,
                  ...(!structuralControlsEnabled ? styles.listWithoutInsertControls : {}),
                }}
                role="list"
                aria-label="Lesson block list"
              >
                {structuralControlsEnabled ? (
                  <SidebarInsertControl
                    insertIndex={0}
                    disabled={disabled}
                    isOpen={openInsertIndex === 0}
                    chooserPosition={chooserPosition}
                    styles={styles}
                    ariaLabel="Insert block at position 1"
                    buttonLabel="+"
                    onOpen={handleOpenInsertChooser}
                    onClose={handleCloseInsertChooser}
                    onSelect={handleInsertBlock}
                    excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                  />
                ) : null}
                {blocks.map((block, index) => (
                  <Fragment key={block.id}>
                    <SortableLessonBlockRow
                      block={block}
                      index={index}
                      disabled={disabled}
                      dragDisabled={isBlockDragDisabled(block)}
                      showDragHandle={!translationMode}
                      isActive={activeSelection === block.id}
                      translationStatus={translationRecordsByBlockId.get(block.id)?.status}
                      clientContextActive={clientContextActive}
                      customizationAccountCount={blockCustomizations?.[block.id]?.length ?? 0}
                      isCustomizationPopoverOpen={openCustomizationBlockId === block.id}
                      styles={styles}
                      onActivate={handleActivateBlock}
                      onReEnable={reEnableBlock}
                      onToggleCustomizationPopover={handleToggleCustomizationPopover}
                    />
                    {structuralControlsEnabled ? (
                      <SidebarInsertControl
                        insertIndex={index + 1}
                        disabled={disabled}
                        isOpen={openInsertIndex === index + 1}
                        chooserPosition={chooserPosition}
                        styles={styles}
                        ariaLabel={`Insert block at position ${index + 2}`}
                        buttonLabel="+"
                        onOpen={handleOpenInsertChooser}
                        onClose={handleCloseInsertChooser}
                        onSelect={handleInsertBlock}
                        excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                      />
                    ) : null}
                  </Fragment>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div style={styles.emptyState}>
          {structuralControlsEnabled ? (
            <>
              <div>Add your first block here or use the canvas controls.</div>
              <div style={styles.emptyStateActions}>
                <SidebarInsertControl
                  insertIndex={0}
                  disabled={disabled}
                  isOpen={openInsertIndex === 0}
                  chooserPosition={chooserPosition}
                  styles={styles}
                  ariaLabel="Add first block"
                  buttonLabel="+"
                  onOpen={handleOpenInsertChooser}
                  onClose={handleCloseInsertChooser}
                  onSelect={handleInsertBlock}
                  excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                />
              </div>
            </>
          ) : (
            <div>This lesson has no blocks.</div>
          )}
        </div>
      )}
      {openCustomizationBlockId && customizationPopoverPosition && customizationPopoverTrigger && (
        <BlockCustomizationPopover
          accounts={openPopoverAccounts}
          expectedCount={openCustomizationAccountCount}
          position={customizationPopoverPosition}
          triggerElement={customizationPopoverTrigger}
          isLoading={assignedAccountsLoading}
          isError={assignedAccountsError}
          onRetry={() => { void refetchAssignedAccounts(); }}
          onClose={handleCloseCustomizationPopover}
          onSelectAccount={(account) => handleSelectCustomizationAccount(openCustomizationBlockId, account)}
        />
      )}
    </aside>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    sidebar: {
      width: '25%',
      minWidth: '260px',
      maxWidth: '340px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.surface,
      borderRight: `1px solid ${tokens.borders.default}`,
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
    },
    headerCopy: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    customizationQueryStatus: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      borderBottom: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.muted,
      fontSize: '12px',
      lineHeight: 1.4,
    },
    customizationRetryButton: {
      padding: 0,
      border: 'none',
      background: 'transparent',
      color: tokens.text.link,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    title: {
      margin: 0,
      fontSize: tokens.typography.mobile.body.size,
      lineHeight: tokens.typography.mobile.body.lineHeight,
      fontWeight: 700,
      color: tokens.text.primary,
    },
    countBadge: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      boxShadow: tokens.shadow.sm,
    },
    listWrapper: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      padding: tokens.spacing.md,
      boxSizing: 'border-box',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    },
    listWithoutInsertControls: {
      gap: tokens.spacing.sm,
    },
    insertControl: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '-10px 0',
      minHeight: 0,
      position: 'relative',
      zIndex: 2,
    },
    insertControlOpen: {
      zIndex: 20,
    },
    insertButtonWrapper: {
      position: 'relative',
      transition: `opacity ${tokens.ui.transitionDefault}`,
    },
    insertButton: {
      width: '28px',
      height: '28px',
      padding: 0,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.muted,
      fontSize: '18px',
      lineHeight: 1,
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: tokens.shadow.sm,
    },
    insertButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    blockRow: {
      display: 'flex',
      alignItems: 'stretch',
      gap: tokens.spacing.sm,
      padding: tokens.spacing.sm,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
      boxSizing: 'border-box',
    },
    blockRowActive: {
      border: `1px solid ${tokens.brand.primary}`,
      boxShadow: tokens.shadow.md,
    },
    blockRowSuppressed: {
      backgroundColor: tokens.backgrounds.app,
      borderStyle: 'dashed',
    },
    rowDisabledNote: {
      fontSize: '11px',
      lineHeight: 1.4,
      fontStyle: 'italic',
      color: tokens.text.subtle,
    },
    clientActionButton: {
      alignSelf: 'center',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      fontSize: '11px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    dragHandle: {
      width: '32px',
      minWidth: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      cursor: 'grab',
      padding: 0,
    },
    dragHandleDisabled: {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
    dragHandleHidden: {
      visibility: 'hidden',
    },
    rowContent: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.xs} ${tokens.spacing.xs} ${tokens.spacing.xs} 0`,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
    },
    rowIndex: {
      width: '20px',
      minWidth: '20px',
      paddingTop: '2px',
      fontSize: '12px',
      fontWeight: 700,
      color: tokens.text.subtle,
      textAlign: 'right',
    },
    rowText: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    translationStatus: {
      marginLeft: 'auto',
      flex: '0 0 auto',
    },
    rowTitle: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: tokens.spacing.xs,
      fontSize: '14px',
      lineHeight: 1.5,
      fontWeight: 600,
      color: tokens.text.primary,
      wordBreak: 'break-word',
    },
    customizedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `1px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.banner.info.border}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.banner.info.border,
      fontSize: '11px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    clientAddedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `1px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: '1px solid #4A3B7A',
      backgroundColor: '#EDE9F7',
      color: '#4A3B7A',
      fontSize: '11px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    rowLockIcon: {
      fontSize: '10px',
      color: tokens.text.muted,
      marginRight: tokens.spacing.xs,
    },
    rowType: {
      fontSize: '12px',
      lineHeight: 1.4,
      color: tokens.text.muted,
    },
    rowSubtitleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.sm,
    },
    // Same neutral weight as the header's countBadge ("N total") — deliberately
    // not the blue customizedBadge/purple clientAddedBadge treatment above.
    customizationChip: {
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: `1px ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.muted,
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: 1.4,
      cursor: 'pointer',
    },
    emptyState: {
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg}`,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      color: tokens.text.muted,
      fontSize: '14px',
      lineHeight: 1.6,
    },
    emptyStateActions: {
      display: 'flex',
      flexDirection: 'column',
    },
  };
}
